import React from 'react';
import styled from 'styled-components';

const ContactSection = styled.section`
  padding: 5rem 0;
  background-color: ${props => props.theme.colors.primary.lighter};
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div``;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.h2};
  margin-bottom: 2rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${props => props.theme.colors.accent.teal};
  }
`;

const ContactText = styled.p`
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.8;
`;

const ContactMethods = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const ContactMethod = styled.div`
  display: flex;
  align-items: center;
`;

const ContactIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.accent.teal};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
  font-size: 1.2rem;
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.text.primary};
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.accent.coral};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary.background};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.text.primary};
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.accent.teal};
    transform: translateY(-3px);
  }
`;

const ContactForm = styled.form`
  background-color: ${props => props.theme.colors.primary.background};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.05);
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.fonts.body};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent.teal};
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.05);
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.fonts.body};
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent.teal};
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.accent.teal};
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.accent.coral};
    transform: translateY(-2px);
  }
`;

const Contact = () => {
  return (
    <ContactSection id="contact">
      <ContactContainer>
        <ContactInfo>
          <SectionTitle>Get In Touch</SectionTitle>
          <ContactText>
            Feel free to reach out if you're looking for a network engineer, have a question, or just want to connect.
          </ContactText>
          <ContactMethods>
            <ContactMethod>
              <ContactIcon>📧</ContactIcon>
              <ContactLink href="mailto:contact@nicholasbentley.com">contact@nicholasbentley.com</ContactLink>
            </ContactMethod>
            <ContactMethod>
              <ContactIcon>📱</ContactIcon>
              <ContactLink href="tel:+61000000000">+61 0000 000 000</ContactLink>
            </ContactMethod>
            <ContactMethod>
              <ContactIcon>📍</ContactIcon>
              <span>Greater Sydney Area, Australia</span>
            </ContactMethod>
          </ContactMethods>
          <SocialLinks>
            <SocialLink href="https://www.linkedin.com/in/nick-bentley/" target="_blank" rel="noopener noreferrer">
              in
            </SocialLink>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              🐦
            </SocialLink>
            <SocialLink href="#" target="_blank" rel="noopener noreferrer">
              📷
            </SocialLink>
          </SocialLinks>
        </ContactInfo>
        <ContactForm>
          <FormGroup>
            <FormLabel htmlFor="name">Name</FormLabel>
            <FormInput type="text" id="name" name="name" required />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="email">Email</FormLabel>
            <FormInput type="email" id="email" name="email" required />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="subject">Subject</FormLabel>
            <FormInput type="text" id="subject" name="subject" required />
          </FormGroup>
          <FormGroup>
            <FormLabel htmlFor="message">Message</FormLabel>
            <FormTextarea id="message" name="message" required></FormTextarea>
          </FormGroup>
          <SubmitButton type="submit">Send Message</SubmitButton>
        </ContactForm>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact;
